import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAudits, saveAudit } from '../utils/storage';
import { PILLARS, calculateNextIndex, getScoreColor, getTier } from '../utils/scoring';
import { formatDate } from '../utils/export';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend
} from 'recharts';

const LOCATION_COLORS = ['#C9A668', '#f87171', '#60a5fa', '#c084fc', '#34d399', '#fb923c', '#fbbf24', '#06b6d4'];

export default function Portfolio() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [comparisonIds, setComparisonIds] = useState([]);

  useEffect(() => {
    getAudits().then(data => {
      setAudits(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="text-center py-20 text-neutral-500">Loading...</div>;

  // Group audits by company tag (or concept name if no tag)
  const companies = {};
  audits.forEach(a => {
    const name = a.intake?.companyTag || a.intake?.conceptName || 'Unknown';
    if (!companies[name]) companies[name] = [];
    companies[name].push(a);
  });

  // Get unique company names for filter
  const companyNames = Object.keys(companies).sort();

  // Get audits to display
  const displayAudits = selectedCompany === 'all'
    ? audits
    : companies[selectedCompany] || [];

  // Comparison mode — selected audits for side-by-side
  const comparisonAudits = comparisonIds.length > 0
    ? audits.filter(a => comparisonIds.includes(a.id))
    : [];

  const toggleComparison = (id) => {
    setComparisonIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 8 ? [...prev, id] : prev
    );
  };

  // Build radar data for comparison
  const radarData = comparisonAudits.length > 0 ? PILLARS.map(p => {
    const entry = { pillar: p.name.length > 12 ? p.name.substring(0, 11) + '...' : p.name };
    comparisonAudits.forEach((a, i) => {
      entry[a.intake?.conceptName || `Location ${i + 1}`] = a.scores?.[p.id]?.score || 0;
    });
    return entry;
  }) : [];

  // Aggregate stats
  const allScores = displayAudits.map(a => calculateNextIndex(a.scores || {}));
  const avgScore = allScores.length > 0 ? allScores.reduce((s, v) => s + v, 0) / allScores.length : 0;
  const bestIdx = allScores.indexOf(Math.max(...allScores));
  const worstIdx = allScores.indexOf(Math.min(...allScores));

  // Best/worst by pillar
  const pillarBestWorst = PILLARS.map(p => {
    const scores = displayAudits.map(a => ({
      name: a.intake?.conceptName,
      score: a.scores?.[p.id]?.score || 0,
    })).filter(s => s.score > 0);
    const sorted = [...scores].sort((a, b) => b.score - a.score);
    return {
      pillar: p.name,
      best: sorted[0] || null,
      worst: sorted[sorted.length - 1] || null,
      avg: scores.length > 0 ? scores.reduce((s, v) => s + v.score, 0) / scores.length : 0,
    };
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-white">Portfolio View</h1>
          <p className="text-xs text-neutral-500">{displayAudits.length} locations — compare performance across properties</p>
        </div>
        <div className="flex gap-2">
          {selectedCompany !== 'all' && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/portfolio/${encodeURIComponent(selectedCompany)}`;
                navigator.clipboard.writeText(url);
                alert('Portfolio link copied!');
              }}
              className="btn-secondary text-sm"
            >
              Share Portfolio
            </button>
          )}
          <Link to="/new" className="btn-primary text-sm">+ New Audit</Link>
        </div>
      </div>

      {/* Filter */}
      {companyNames.length > 1 && (
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setSelectedCompany('all')}
            className={`text-xs px-3 py-1.5 rounded-lg transition-all ${selectedCompany === 'all' ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
          >
            All ({audits.length})
          </button>
          {companyNames.map(name => (
            <button
              key={name}
              onClick={() => setSelectedCompany(name)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-all ${selectedCompany === name ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-400 hover:text-white'}`}
            >
              {name} ({companies[name].length})
            </button>
          ))}
        </div>
      )}

      {/* Company Branding — when a specific company is selected */}
      {selectedCompany !== 'all' && (() => {
        const companyAudits = companies[selectedCompany] || [];
        const firstAudit = companyAudits[0];
        const companyLogo = firstAudit?.branding?.clientLogo || '';
        const companyName = firstAudit?.branding?.companyName || selectedCompany;

        const updateCompanyLogo = (logoUrl) => {
          companyAudits.forEach(a => {
            const updated = { ...a, branding: { ...(a.branding || {}), clientLogo: logoUrl, companyName: selectedCompany, source: 'company' } };
            saveAudit(updated);
          });
          // Refresh
          getAudits().then(setAudits);
        };

        return (
          <div className="card p-4 mb-6 flex items-center gap-4">
            {companyLogo ? (
              <img src={companyLogo} alt={companyName} className="h-12 w-auto max-w-[150px] object-contain bg-white/5 rounded p-1" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-neutral-800 flex items-center justify-center text-neutral-600 text-xs">Logo</div>
            )}
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-white">{companyName}</h2>
              <p className="text-[10px] text-neutral-500">{companyAudits.length} location{companyAudits.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                defaultValue={companyLogo?.startsWith('data:') ? '' : (companyLogo || '')}
                onBlur={e => { if (e.target.value.trim()) updateCompanyLogo(e.target.value.trim()); }}
                placeholder="Logo URL..."
                className="input-field text-[10px] py-1.5 w-48"
              />
              <label className="text-[10px] px-2 py-1.5 rounded bg-neutral-800/50 text-neutral-500 hover:text-neutral-300 border border-dashed border-neutral-700 cursor-pointer transition-colors">
                Upload
                <input type="file" accept="image/*" className="hidden" onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () => updateCompanyLogo(reader.result);
                  reader.readAsDataURL(file);
                }} />
              </label>
              {companyLogo && (
                <button onClick={() => updateCompanyLogo('')} className="text-[9px] text-neutral-600 hover:text-red-400">Remove</button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Aggregate Stats */}
      {displayAudits.length > 1 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="card p-4 text-center">
            <p className="text-2xl font-black text-brand-gold">{avgScore.toFixed(1)}</p>
            <p className="text-[10px] text-neutral-500">Portfolio Average</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-black text-white">{displayAudits.length}</p>
            <p className="text-[10px] text-neutral-500">Locations</p>
          </div>
          {displayAudits[bestIdx] && (
            <div className="card p-4 text-center">
              <p className="text-sm font-bold text-emerald-400">{displayAudits[bestIdx].intake?.conceptName}</p>
              <p className="text-lg font-black text-emerald-400">{allScores[bestIdx]?.toFixed(1)}</p>
              <p className="text-[10px] text-neutral-500">Top Performer</p>
            </div>
          )}
          {displayAudits[worstIdx] && displayAudits.length > 1 && (
            <div className="card p-4 text-center">
              <p className="text-sm font-bold text-red-400">{displayAudits[worstIdx].intake?.conceptName}</p>
              <p className="text-lg font-black text-red-400">{allScores[worstIdx]?.toFixed(1)}</p>
              <p className="text-[10px] text-neutral-500">Needs Attention</p>
            </div>
          )}
        </div>
      )}

      {/* Location Cards */}
      <div className="space-y-2 mb-8">
        {displayAudits.map((a, i) => {
          const index = calculateNextIndex(a.scores || {});
          const color = getScoreColor(index);
          const tier = getTier(index);
          const isComparing = comparisonIds.includes(a.id);

          return (
            <div key={a.id} className={`card p-4 flex items-center gap-4 transition-all ${isComparing ? 'border-brand-gold/40' : 'hover:border-brand-gold/20'}`}>
              <input
                type="checkbox"
                checked={isComparing}
                onChange={() => toggleComparison(a.id)}
                className="rounded border-neutral-600 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30 flex-shrink-0"
              />
              <div
                className="w-2 h-10 rounded-full flex-shrink-0"
                style={{ backgroundColor: isComparing ? LOCATION_COLORS[comparisonIds.indexOf(a.id) % LOCATION_COLORS.length] : 'transparent' }}
              />
              <Link to={`/dashboard/${a.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-lg ${color.bg} flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-lg font-black ${color.text}`}>{index.toFixed(1)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{a.intake?.conceptName}</p>
                  <p className="text-xs text-neutral-500">{a.intake?.city} — {formatDate(a.createdAt)}</p>
                </div>
                <span className="text-xs px-2 py-1 rounded font-medium hidden sm:inline-block"
                  style={{ backgroundColor: tier.color + '20', color: tier.color }}>
                  {tier.name}
                </span>
              </Link>
            </div>
          );
        })}
      </div>

      {/* Comparison Radar */}
      {comparisonAudits.length >= 2 && (
        <div className="card p-6 mb-6">
          <h2 className="font-display text-lg text-white mb-1">Location Comparison</h2>
          <p className="text-xs text-neutral-500 mb-4">Select locations above to compare side-by-side</p>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-4">
            {comparisonAudits.map((a, i) => (
              <span key={a.id} className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LOCATION_COLORS[i % LOCATION_COLORS.length] }} />
                {a.intake?.conceptName} ({calculateNextIndex(a.scores || {}).toFixed(1)})
              </span>
            ))}
          </div>

          <div style={{ height: 400 }}>
            <ResponsiveContainer>
              <RadarChart cx="50%" cy="50%" outerRadius="75%" data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="pillar" tick={{ fill: '#a3a3a3', fontSize: 10 }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#525252', fontSize: 9 }} />
                <Tooltip contentStyle={{ backgroundColor: '#171717', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                {comparisonAudits.map((a, i) => (
                  <Radar
                    key={a.id}
                    name={a.intake?.conceptName || `Location ${i + 1}`}
                    dataKey={a.intake?.conceptName || `Location ${i + 1}`}
                    stroke={LOCATION_COLORS[i % LOCATION_COLORS.length]}
                    fill={LOCATION_COLORS[i % LOCATION_COLORS.length]}
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                ))}
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Pillar comparison table */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left text-neutral-500 pb-2 pr-3">Pillar</th>
                  {comparisonAudits.map((a, i) => (
                    <th key={a.id} className="text-center pb-2 px-2" style={{ color: LOCATION_COLORS[i % LOCATION_COLORS.length] }}>
                      {(a.intake?.conceptName || '').substring(0, 15)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PILLARS.map(p => (
                  <tr key={p.id} className="border-t border-neutral-800/50">
                    <td className="text-neutral-300 py-2 pr-3">{p.name}</td>
                    {comparisonAudits.map(a => {
                      const s = a.scores?.[p.id]?.score || 0;
                      const color = getScoreColor(s);
                      return (
                        <td key={a.id} className={`text-center py-2 px-2 font-bold ${color.text}`}>
                          {s > 0 ? s.toFixed(1) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t-2 border-neutral-700">
                  <td className="text-white font-bold py-2 pr-3">Next Index</td>
                  {comparisonAudits.map(a => {
                    const idx = calculateNextIndex(a.scores || {});
                    const color = getScoreColor(idx);
                    return (
                      <td key={a.id} className={`text-center py-2 px-2 font-black text-lg ${color.text}`}>
                        {idx.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {comparisonAudits.length === 1 && (
        <div className="card p-6 text-center">
          <p className="text-neutral-500 text-sm">Select at least 2 locations to compare</p>
        </div>
      )}

      {/* Best/Worst by Pillar */}
      {displayAudits.length > 1 && (
        <div className="card p-6">
          <h2 className="font-display text-lg text-white mb-4">Performance by Pillar</h2>
          <div className="space-y-2">
            {pillarBestWorst.map(p => (
              <div key={p.pillar} className="flex items-center gap-3 text-xs py-1.5 border-b border-neutral-800/30">
                <span className="text-neutral-300 w-40 truncate">{p.pillar}</span>
                <span className="text-neutral-600 w-12 text-center">{p.avg > 0 ? p.avg.toFixed(1) : '—'}</span>
                {p.best && (
                  <span className="text-emerald-400 flex-1 truncate">↑ {p.best.name} ({p.best.score.toFixed(1)})</span>
                )}
                {p.worst && p.best?.name !== p.worst?.name && (
                  <span className="text-red-400 flex-1 truncate">↓ {p.worst.name} ({p.worst.score.toFixed(1)})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
